import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { SupportedCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BodyM, LabelM } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { isCryptocurrency } from '@sorare/core/src/lib/cryptocurrency';
import { glossary } from '@sorare/core/src/lib/glossary';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { WalletPaymentMethod } from '@sorare/core/src/lib/paymentMethod';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { SelectedPaymentMethodForConfirmation } from 'components/buyActions/PaymentBox/Methods/SelectedPaymentMethodForConfirmation';
import { TokenSummary } from 'components/buyActions/TokenSummary';
import {
  MarketFeeStatus,
  isMarketFeeEnabled,
  useMarketFeesHelperStatus,
} from 'hooks/useMarketFeesHelperStatus';

import { FeesDetailsTooltip } from '../FeesDetailsTooltip';
import { MonetaryCard } from '../MonetaryCard';
import type { OfferDealSummary_anyCard } from './__generated__/index.graphql';

const messages = defineMessages({
  amount: {
    id: 'DealSummary.amount',
    defaultMessage: 'Amount',
  },
  details: {
    id: 'DealSummary.details',
    defaultMessage: 'Offer details',
  },
  duration: {
    id: 'DealSummary.duration',
    defaultMessage: 'Offer duration',
  },
  durationWarnings: {
    id: 'DealSummary.duration.warning',
    defaultMessage:
      'The Cards and money you send in an offer are held. You cannot include the Cards or money in another offer.',
  },
  nbDays: {
    id: 'DealSummary.duration.nbDays',
    defaultMessage: '{nbDays, plural, one {# day} other {# days}}',
  },
  nothing: {
    id: 'DealSummary.section.nothing',
    defaultMessage: 'Nothing',
  },
});

type Props = {
  withEmpty?: boolean;
  receiver?: ReactNode;
  sender?: ReactNode;
  validationMessages?: Record<string, ReactNode>;
  sendAmount?: MonetaryAmount;
  sendCards?: OfferDealSummary_anyCard[];
  receiveAmount?: MonetaryAmount;
  marketFeeAmount?: MonetaryAmount;
  receiveCards?: OfferDealSummary_anyCard[];
  sendAmountCurrency: SupportedCurrency;
  receiveAmountCurrency: SupportedCurrency;
  duration?: number;
  paymentMethod: WalletPaymentMethod | null;
  withMonetaryCard?: boolean;
};

const NothingWarning = styled.div`
  padding: var(--unit) var(--double-unit);
  border-radius: var(--unit);
  background: rgba(var(--c-rgb-red-400), 0.05);
  border: var(--c-red-400) solid 1px;
`;
const UppercaseText16 = styled(BodyM)`
  text-transform: uppercase;
`;

const Nothing = () => {
  return (
    <NothingWarning>
      <UppercaseText16>
        <FormattedMessage {...messages.nothing} />
      </UppercaseText16>
    </NothingWarning>
  );
};

type TokensProps = {
  cards: OfferDealSummary_anyCard[];
  validationMessages?: Record<string, ReactNode>;
};

const WarningWrapper = styled.div`
  padding: var(--double-unit) 0 var(--unit);
`;

const ItemWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  border-radius: var(--unit);
  background: var(--c-nd-100);
  border: var(--c-nd-200) solid 1px;
  padding: var(--double-unit);
`;

const Tokens = ({ cards, validationMessages }: TokensProps) => {
  return (
    <>
      {cards.map(card => (
        <ItemWrapper key={card.slug}>
          <TokenSummary small card={card} withoutRecentSales />
          {!!validationMessages?.[card.slug] && (
            <WarningWrapper>{validationMessages[card.slug]}</WarningWrapper>
          )}
        </ItemWrapper>
      ))}
    </>
  );
};

const AmountLabel = styled(BodyL)`
  font-style: italic;
`;

const ExponentLabel = styled(LabelM)`
  color: var(--c-nd-600);
`;

type AmountsProps = {
  amount: MonetaryAmount;
  marketFeeAmount?: MonetaryAmount;
  marketFeeStatus: MarketFeeStatus;
  referenceCurrency: SupportedCurrency;
  withMonetaryCard?: boolean;
};

const Amounts = ({
  amount,
  marketFeeStatus = MarketFeeStatus.DISABLED,
  marketFeeAmount,
  referenceCurrency,
  withMonetaryCard = false,
}: AmountsProps) => {
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: amount,
    primaryCurrency: isCryptocurrency(referenceCurrency)
      ? Currency.ETH
      : Currency.FIAT,
  });
  if (amount.isZero) return null;

  const isFeeEnabled = isMarketFeeEnabled(marketFeeStatus);
  if (withMonetaryCard)
    return (
      <ItemWrapper>
        <MonetaryCard
          amount={{ ...amount, referenceCurrency }}
          marketFeeAmount={marketFeeAmount}
          displayFees={isFeeEnabled}
          marketFeeStatus={marketFeeStatus}
        />
      </ItemWrapper>
    );
  return (
    <ItemWrapper>
      <AmountLabel bold>{main}</AmountLabel>
      {!!(
        (exponent && !onlyShowFiatCurrency) ||
        (marketFeeAmount && isFeeEnabled)
      ) && (
        <Horizontal>
          {!!exponent && !onlyShowFiatCurrency && (
            <ExponentLabel>{`≈ ${exponent}`}</ExponentLabel>
          )}
          {!!marketFeeAmount && !!isFeeEnabled && (
            <FeesDetailsTooltip
              monetaryAmount={amount}
              marketFeeMonetaryAmount={marketFeeAmount}
              referenceCurrency={referenceCurrency}
              marketFeeStatus={marketFeeStatus}
            />
          )}
        </Horizontal>
      )}
    </ItemWrapper>
  );
};

const DurationWarnings = styled.div`
  padding: var(--unit) var(--double-unit);
  border-radius: var(--unit);
  background: var(--c-nd-100);
  border: var(--c-nd-200) solid 1px;
`;

const OfferDetails = ({ duration }: Pick<Props, 'duration'>) => {
  if (!duration) {
    return null;
  }
  return (
    <Vertical>
      <div>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...messages.duration} />
        </LabelM>
        <BodyM>
          <FormattedMessage
            {...messages.nbDays}
            values={{ nbDays: duration }}
          />
        </BodyM>
      </div>
      <DurationWarnings>
        <LabelM>
          <FormattedMessage {...messages.durationWarnings} />
        </LabelM>
      </DurationWarnings>
    </Vertical>
  );
};

type OfferLegDescriptionProps = {
  withEmpty: boolean;
  title: ReactNode;
  amount: MonetaryAmount;
  marketFeeStatus?: MarketFeeStatus;
  marketFeeAmount?: MonetaryAmount;
  cards: OfferDealSummary_anyCard[];
  validationMessages?: Record<string, ReactNode>;
  paymentMethod?: WalletPaymentMethod | null;
  referenceCurrency: SupportedCurrency;
  withMonetaryCard?: boolean;
};

const Description = styled(Vertical)`
  width: 100%;
`;

const OfferLegDescription = ({
  withEmpty,
  amount,
  marketFeeAmount,
  title,
  cards,
  marketFeeStatus = MarketFeeStatus.DISABLED,
  validationMessages,
  paymentMethod,
  referenceCurrency,
  withMonetaryCard = false,
}: OfferLegDescriptionProps) => {
  const empty = amount.isZero && cards.length === 0;

  if (empty && !withEmpty) {
    return null;
  }
  return (
    <Description>
      {title}
      {empty ? (
        <Nothing />
      ) : (
        <>
          <Tokens cards={cards} validationMessages={validationMessages} />
          <Amounts
            amount={amount}
            marketFeeAmount={marketFeeAmount}
            marketFeeStatus={marketFeeStatus}
            referenceCurrency={referenceCurrency}
            withMonetaryCard={withMonetaryCard}
          />
          {!!amount.isPositive && !!paymentMethod && (
            <>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage {...glossary.payWith} />
              </LabelM>
              <SelectedPaymentMethodForConfirmation
                paymentMethod={paymentMethod}
                paymentCurrency={
                  paymentMethod === WalletPaymentMethod.CRYPTO_WALLET
                    ? Currency.ETH
                    : Currency.FIAT
                }
              />
            </>
          )}
        </>
      )}
    </Description>
  );
};

const OfferRow = styled.div`
  display: flex;
  gap: var(--double-unit);
  flex-direction: column;
  @media ${tabletAndAbove} {
    flex-direction: row;
    padding-bottom: var(--double-unit);
    border-bottom: var(--c-nd-100) solid 1px;
  }
`;

export const OfferDealSummary = ({
  sendAmount = MonetaryAmount.zero,
  sendAmountCurrency,
  sendCards = [],
  receiveAmount = MonetaryAmount.zero,
  marketFeeAmount = MonetaryAmount.zero,
  receiveAmountCurrency,
  receiveCards = [],
  withEmpty = false,
  duration,
  sender,
  receiver,
  validationMessages,
  paymentMethod,
  withMonetaryCard,
}: Props) => {
  const marketFeeStatus = useMarketFeesHelperStatus(sendCards);
  return (
    <Vertical gap={2}>
      <OfferRow>
        <OfferLegDescription
          withEmpty={withEmpty}
          title={sender}
          amount={sendAmount}
          cards={sendCards}
          validationMessages={validationMessages}
          paymentMethod={paymentMethod}
          referenceCurrency={sendAmountCurrency}
          withMonetaryCard={withMonetaryCard}
        />
        <OfferLegDescription
          withEmpty={withEmpty}
          title={receiver}
          amount={receiveAmount}
          marketFeeStatus={marketFeeStatus}
          marketFeeAmount={marketFeeAmount}
          cards={receiveCards}
          referenceCurrency={receiveAmountCurrency}
          withMonetaryCard={withMonetaryCard}
        />
      </OfferRow>
      <OfferDetails duration={duration} />
    </Vertical>
  );
};

OfferDealSummary.fragments = {
  anyCard: gql`
    fragment OfferDealSummary_anyCard on AnyCardInterface {
      slug
      ...TokenSummary_anyCard
      ...useMarketFeesHelperStatus_anyCard
    }
    ${useMarketFeesHelperStatus.fragments.anyCard}
    ${TokenSummary.fragments.anyCard}
  ` as TypedDocumentNode<OfferDealSummary_anyCard>,
};
